import React from 'react'
import { motion } from 'framer-motion'
import { useParams, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { ArrowRight, CheckCircle } from 'lucide-react'

const ServicePage = () => {
  const { serviceSlug } = useParams()

  const services = {
    'devops': {
      title: 'DevOps',
      subtitle: 'Automate and optimize your development workflow with CI/CD pipelines, containerization, and infrastructure as code',
      description: 'Transform your development workflow with our comprehensive DevOps expertise. We help you automate processes, improve collaboration, and deliver software faster and more reliably. From CI/CD pipeline automation to Kubernetes orchestration, we implement modern DevOps practices that streamline your entire software delivery lifecycle from code to production.',
      features: [
        'CI/CD pipeline automation and optimization (GitHub Actions, GitLab CI, Jenkins, Azure DevOps)',
        'Infrastructure as Code implementation (Terraform, Ansible, CloudFormation)',
        'Container orchestration with Kubernetes and Docker',
        'Monitoring and alerting systems (Prometheus, Grafana, Datadog)',
        'Performance optimization and tuning',
        'Security integration in DevOps processes (DevSecOps)',
        'Automated testing and quality assurance',
        'Multi-cloud deployment strategies'
      ],
      benefits: [
        'Faster time to market with automated deployments',
        'Improved code quality through CI/CD practices',
        'Reduced deployment risks with automated testing',
        'Better team collaboration and communication',
        'Increased system reliability and uptime',
        'Scalable infrastructure that grows with your needs'
      ]
    },
    'custom-software-development': {
      title: 'Custom Software Development',
      subtitle: 'Build web applications, mobile apps, APIs, and enterprise software solutions tailored to your business',
      description: 'From web applications to mobile apps and enterprise software solutions, we build applications tailored to your specific business needs. Our development services focus on creating scalable, maintainable, and user-friendly applications using modern frameworks and technologies. Whether you need a React web app, a Node.js API, or a mobile application, we deliver solutions that drive real business value.',
      features: [
        'Full-stack web application development (React, Next.js, Vue.js, Angular)',
        'Mobile application development (React Native, Flutter, native iOS/Android)',
        'API design and integration services (REST, GraphQL, microservices)',
        'Backend development (Node.js, Python, .NET, Java)',
        'Database design and optimization (PostgreSQL, MongoDB, Redis)',
        'Third-party integrations and API connections',
        'Responsive and accessible design',
        'Performance optimization and scalability planning'
      ],
      benefits: [
        'Custom solutions designed for your specific needs',
        'Modern, maintainable codebase using best practices',
        'Scalable architecture that grows with your business',
        'Seamless integration with existing systems',
        'Cross-platform compatibility (web, mobile, desktop)',
        'Ongoing support and maintenance'
      ]
    },
    'architecture': {
      title: 'Architecture',
      subtitle: 'Design secure, scalable cloud infrastructure and system architectures across AWS, Azure, GCP, and multi-cloud environments',
      description: 'From AWS to Azure, GCP, and multi-cloud strategies, we design architectures that grow with you. Our architecture services help you build robust, secure, and scalable foundations that support your business objectives. We create systems that are both powerful today and adaptable for tomorrow, with a focus on cloud infrastructure, microservices, scalability, and security-first design principles.',
      features: [
        'Cloud architecture design and implementation (AWS, Azure, GCP)',
        'Multi-cloud strategy planning and execution',
        'Microservices architecture design and implementation',
        'Scalable infrastructure design for high-traffic applications',
        'Security-first approach with compliance (SOC 2, HIPAA, PCI-DSS)',
        'Migration planning and execution (lift-and-shift, refactoring, rearchitecting)',
        'Performance monitoring and optimization',
        'Disaster recovery and high availability planning'
      ],
      benefits: [
        'Reduced infrastructure costs through optimization',
        'Improved scalability and flexibility',
        'Enhanced security and compliance',
        'Better performance and reliability',
        'Future-proof architecture that adapts to change',
        'Multi-cloud resilience and vendor independence'
      ]
    }
  }

  const service = services[serviceSlug]

  if (!service) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Header />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service Not Found</h1>
          <p className="text-gray-300 mb-8">The service you're looking for doesn't exist.</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-36">
        {/* Hero Section */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
                {service.title}
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-6 leading-relaxed">
                {service.subtitle}
              </p>
              <p className="text-lg text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section-padding bg-gray-900/30">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white text-center mb-12">
                What We Offer
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-background/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-gray-300 leading-relaxed">{feature}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white text-center mb-12">
                Benefits
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-6 text-center"
                  >
                    <h3 className="text-xl font-semibold text-white mb-2">{benefit}</h3>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gray-900/30">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-4xl font-heading font-bold text-white mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let's discuss how our {service.title.toLowerCase()} services can help your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4"
                >
                  Contact Us
                  <ArrowRight size={20} />
                </motion.a>
                <Link
                  to="/applications/cloud-chekr"
                  className="btn-secondary inline-flex items-center gap-2 text-lg px-10 py-4"
                >
                  View Our Applications
                  <ArrowRight size={20} />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default ServicePage
